/**
 * One statement's fields on the account's RFC sheet (§5.27, kit 67): slug and
 * its own status, the level as a choice of five, the text with its count, why,
 * its paths and its check script — each refusal said under its field as how
 * to fix it. Split out of `rules-sheet.tsx`, with the labelled field both use.
 */
import type React from 'react';
import { RULE_LEVELS, STATEMENT_STATUSES, type RuleRfc } from '@airlock/contracts/rules/document';
import { Input } from '../../../components/primitives';
import { color, font } from '../../../theme/tokens';
import { selectStyle, textareaStyle } from '../shared';
import { CheckFields } from './rules-check';

type Statement = RuleRfc['statements'][number];

export const mono13: React.CSSProperties = { fontFamily: font.mono, fontSize: 13 };

export function StatementFields({
  origin,
  rfc,
  rfcStatus,
  value,
  onChange,
  said,
  onLeave,
  scripts
}: {
  origin: string;
  rfc: number;
  rfcStatus: string;
  value: Statement;
  onChange: (next: Statement) => void;
  said: (field: string) => string | null;
  onLeave: (field: string) => void;
  scripts: { text: (path: string) => string | undefined; put: (path: string, text: string) => void };
}): React.JSX.Element {
  const put = (patch: Partial<Statement>): void => {
    const next = { ...value, ...patch } as Statement;
    for (const key of Object.keys(next) as (keyof Statement)[]) if (next[key] === undefined) delete next[key];
    onChange(next);
  };
  const count = value.text.trim().length;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 240px) minmax(0, 260px)', gap: 12 }}>
        <RuleField label="Slug" error={said('slug')} hint={value.slug ? `Cited ${origin}:${rfc}/${value.slug}.` : 'Its id, in kebab-case.'}>
          <Input value={value.slug} onBlur={() => onLeave('slug')} onChange={(e) => put({ slug: e.target.value.trim() })} style={mono13} />
        </RuleField>
        <RuleField label="Status (optional)" error={said('status')} hint="Only for a statement the RFC does not carry along.">
          <select value={value.status ?? ''} onChange={(e) => put({ status: (e.target.value || undefined) as Statement['status'] })} style={selectStyle}>
            <option value="">As the RFC — {rfcStatus}</option>
            {STATEMENT_STATUSES.map((one) => (
              <option key={one} value={one}>
                {one}
              </option>
            ))}
          </select>
        </RuleField>
      </div>
      <div role="radiogroup" aria-label="Level" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {RULE_LEVELS.map((one) => {
          const on = value.level === one;
          return (
            <button
              key={one}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => put({ level: one })}
              style={{ padding: '6px 11px', border: `1px solid ${on ? color.accentRule : color.lineInput}`, borderRadius: 8, background: on ? color.accentWash : color.surface, color: on ? color.accentText : color.inkMuted, fontFamily: font.mono, fontSize: 12, cursor: 'pointer' }}
            >
              {one}
            </button>
          );
        })}
      </div>
      <RuleField label={`Text · ${count} / 4000`} error={said('text')} hint="With its level in capitals, as RFC 2119 writes it.">
        <textarea value={value.text} rows={count > 400 ? 8 : 3} onBlur={() => onLeave('text')} onChange={(e) => put({ text: e.target.value })} style={{ ...textareaStyle, fontFamily: 'inherit', fontSize: 13.5 }} />
      </RuleField>
      <RuleField label="Why (optional)" error={said('why')} hint="Why the rule exists — the session's agent and the auditor read it beside the rule.">
        <textarea value={value.why ?? ''} rows={2} placeholder="why the rule exists" onChange={(e) => put({ why: e.target.value || undefined })} style={{ ...textareaStyle, fontFamily: 'inherit', fontSize: 13.5 }} />
      </RuleField>
      <RuleField label="Paths (optional)" error={said('paths')} hint="Narrower than the RFC’s; left empty, the RFC’s.">
        <Input value={(value.paths ?? []).join(', ')} placeholder="globs, comma-separated" onChange={(e) => put({ paths: globs(e.target.value) })} style={mono13} />
      </RuleField>
      <CheckFields
        slug={value.slug}
        check={value.check}
        error={said('check')}
        script={value.check ? scripts.text(value.check) : undefined}
        onCheck={(check) => put({ check })}
        onScript={scripts.put}
        Field={RuleField}
      />
    </>
  );
}

/** A labelled field with its hint, or how to fix it in place of it. */
export function RuleField({ label, hint, error, children }: { label: string; hint?: string; error: string | null; children: React.ReactNode }): React.JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: 12.5, color: color.inkMuted }}>{label}</span>
      {children}
      {(error || hint) && (
        <span role={error ? 'alert' : undefined} style={{ fontSize: 12, lineHeight: 1.5, color: error ? color.badText : color.inkFaint, overflowWrap: 'anywhere' }}>
          {error ?? hint}
        </span>
      )}
    </label>
  );
}

/** Globs typed comma-separated; none is no list at all. */
export function globs(text: string): string[] | undefined {
  const list = text
    .split(/[\n,]/)
    .map((one) => one.trim())
    .filter(Boolean);
  return list.length ? list : undefined;
}

